import { useProjectStore } from '@/store/project-store'
import { AvailableEffect, EffectGroupName } from '@/static/effectName'
import { allLayers, Layers } from '@/static/Layers'
import { Effect, EffectState } from '@/pixi/filters/types'
import useLayers from '@/composables/UseLayers'
import * as PIXI from 'pixi.js'
import { EffectsFactory } from '@/pixi/EffectsFactory'

// Stores the effects that are applied to the various layers
export const activeEffects = new Map<EffectGroupName, Effect>()

/**
 * Manages the modification, removal, or addition of Effects to Canvas Layers.
 * This includes modifying the states in the store, and the map of activeEffects itself.
 * @returns the composable
 */
export default function useEffect () {

  const effectsFactory = new EffectsFactory()
  const projectStore = useProjectStore()
  const { canvasLayers } = useLayers()

  const createEffectState = (availableEffect: AvailableEffect): EffectState => {
    const initialState: EffectState = {
      appliedToLayer: availableEffect.config.layers,
      audioReactivityEnabled: availableEffect.config.isAudioReactivityEnabledByDefault,
      intensity: availableEffect.config.defaultIntensityValue,
      name: availableEffect.name,
      groupName: availableEffect.groupName,
      isLocked: false
    }
    return JSON.parse(JSON.stringify(initialState))
  }

  const registerEffect = (effectState: EffectState): Effect => {
    projectStore.activeEffectStates.set(effectState.groupName, effectState)
    const effect = effectsFactory.createEffect(effectState)
      
    if (effect.updateFilterCallback) {
      PIXI.Ticker.shared.add(effect.updateFilterCallback)
    }
    return effect
  }

  const applyEffectsToLayers = (layers = allLayers) => {
    const activeEffectsArray = [...activeEffects.values()]
    layers.forEach(layer => {
      const layerEffects = activeEffectsArray.filter(effect => projectStore.activeEffectStates.get(effect.groupName)?.appliedToLayer.indexOf(layer) !== -1)
      canvasLayers[layer].updateEffects(layerEffects)
    })
  }

  const addEffect = (availableEffect: AvailableEffect): void => {
    let effectState = projectStore.activeEffectStates.get(availableEffect.groupName)

    if (!effectState) {
      effectState = createEffectState(availableEffect)
      activeEffects.set(availableEffect.groupName, registerEffect(effectState))
    } else if (effectState.name !== availableEffect.name) {
      effectState = { ...projectStore.selectedEffect, name: availableEffect.name } as EffectState
      activeEffects.set(availableEffect.groupName, registerEffect(effectState))
    }
    applyEffectsToLayers()
    projectStore.selectedEffect = effectState
  }

  const updateEffectLayer = (layer: Layers): void => {
    toggleEffectLayer(layer)
    applyEffectsToLayers([layer])
  }

  const removeEffect = (effectGroupName: EffectGroupName) => {
    const effectState = projectStore.activeEffectStates.get(effectGroupName)
    const effect = activeEffects.get(effectGroupName)
    if (effectState && effect) {
      PIXI.Ticker.shared.remove(effect.updateFilterCallback)
      projectStore.activeEffectStates.delete(effectGroupName)
      activeEffects.delete(effectGroupName)
      applyEffectsToLayers(effectState.appliedToLayer)
    }
  }

  const removeAllEffects = () => {
    projectStore.activeEffectStates.forEach(effectState => {
      removeEffect(effectState.groupName)
    })
  }

  const toggleEffectLayer = (layer: Layers): void => {
    const index = projectStore.selectedEffect.appliedToLayer.indexOf(layer)
    if (index > -1) {
      projectStore.selectedEffect.appliedToLayer.splice(index, 1)
    } else {
      projectStore.selectedEffect.appliedToLayer.push(layer)
    }
  }
  
  return {
    addEffect,
    updateEffectLayer,
    removeEffect,
    removeAllEffects,
    applyEffectsToLayers
  }
}

